import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Award, Share2, Sparkles, ArrowRight, BookOpen, CheckCircle, Star, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CourseCompletionCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName: string;
  completedAt?: string;
  modulesSummary?: {
    passed: number;
    total: number;
  };
}

// Enhanced confetti particle with more variety
const ConfettiParticle: React.FC<{ delay: number; x: number; shape?: 'circle' | 'square' | 'star' }> = ({ delay, x, shape = 'circle' }) => {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#FF9FF3', '#54E346'];
  const color = colors[Math.floor(Math.random() * colors.length)];
  
  const getShapeClass = () => {
    switch (shape) {
      case 'square': return 'rounded-sm';
      case 'star': return 'clip-path-star';
      default: return 'rounded-full';
    }
  };
  
  return (
    <motion.div
      className={`absolute w-3 h-3 ${getShapeClass()}`}
      style={{ 
        backgroundColor: color,
        left: `${x}%`,
        top: '-10px'
      }}
      initial={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
      animate={{ 
        y: 600, 
        opacity: 0, 
        rotate: Math.random() * 1080 - 540,
        scale: Math.random() * 0.5 + 0.5,
        x: (Math.random() - 0.5) * 100
      }}
      transition={{ 
        duration: 3.5 + Math.random() * 2.5,
        delay: delay,
        ease: "easeOut"
      }}
    />
  );
};

// Sparkle burst component
const SparkleBurst: React.FC = () => {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: 8 }, (_, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2"
          style={{
            transform: `rotate(${i * 45}deg)`,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
          transition={{ delay: 0.5, duration: 1.5, repeat: 2 }}
        >
          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" style={{ transform: `translateX(60px)` }} />
        </motion.div>
      ))}
    </div>
  );
};

export const CourseCompletionCelebration: React.FC<CourseCompletionCelebrationProps> = ({
  isOpen,
  onClose,
  courseId,
  courseName,
  completedAt,
  modulesSummary
}) => {
  const navigate = useNavigate();
  const [confettiPieces, setConfettiPieces] = useState<Array<{ id: number; delay: number; x: number; shape: 'circle' | 'square' | 'star' }>>([]);

  useEffect(() => {
    if (isOpen) {
      // Generate more varied confetti pieces
      const shapes: ('circle' | 'square' | 'star')[] = ['circle', 'square', 'star'];
      const pieces = Array.from({ length: 80 }, (_, i) => ({
        id: i,
        delay: Math.random() * 0.8,
        x: Math.random() * 100,
        shape: shapes[i % shapes.length]
      }));
      setConfettiPieces(pieces);
    }
  }, [isOpen]);

  const handleViewCertificate = () => {
    onClose();
    navigate(`/certificates?course=${courseId}`);
  };

  const handleViewMarksheet = () => {
    onClose();
    navigate(`/my-courses/${courseId}#marksheet`);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Course Completed!',
          text: `🎉 I just completed ${courseName}! Another step in my learning journey.`,
          url: window.location.origin
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    }
  };

  const handleExploreCourses = () => {
    onClose();
    navigate('/courses');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        {/* Enhanced confetti container */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <AnimatePresence>
            {confettiPieces.map((piece) => (
              <ConfettiParticle key={piece.id} delay={piece.delay} x={piece.x} shape={piece.shape} />
            ))}
          </AnimatePresence>
        </div>

        {/* Sparkle burst effect */}
        <SparkleBurst />

        <DialogHeader className="text-center pt-6">
          {/* Animated trophy with enhanced glow */}
          <motion.div
            className="mx-auto mb-4 relative"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          >
            {/* Pulsing glow */}
            <motion.div
              className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 blur-xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            
            <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 flex items-center justify-center shadow-2xl">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Trophy className="h-14 w-14 text-white drop-shadow-lg" />
              </motion.div>
            </div>
            
            <motion.div
              className="absolute -top-2 -right-2"
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.6, type: "spring" }}
            >
              <Sparkles className="h-10 w-10 text-yellow-400" />
            </motion.div>
            
            <motion.div
              className="absolute -bottom-1 -left-2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7, type: "spring" }}
            >
              <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 bg-clip-text text-transparent">
              🎉 Congratulations! 🎉
            </DialogTitle>
            <p className="text-muted-foreground mt-2 text-lg">
              You've successfully completed
            </p>
            <p className="text-xl font-bold text-foreground mt-1">
              {courseName}
            </p>
          </motion.div>
        </DialogHeader>

        <motion.div
          className="space-y-4 py-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {/* Modules Summary - if available */}
          {modulesSummary && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-4 p-3 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-semibold">{modulesSummary.passed}/{modulesSummary.total}</span>
                <span className="text-muted-foreground text-sm">Modules Passed</span>
              </div>
            </motion.div>
          )}

          {/* Achievement badge */}
          <motion.div 
            className="flex items-center justify-center gap-3 p-4 rounded-lg bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border border-green-500/20"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Award className="h-10 w-10 text-green-500" />
            </motion.div>
            <div className="text-left">
              <p className="font-bold text-foreground text-lg">Certificate Earned!</p>
              <p className="text-sm text-muted-foreground">
                {completedAt ? new Date(completedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </motion.div>

          {/* Action buttons */}
          <motion.div 
            className="space-y-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Button 
              onClick={handleViewCertificate} 
              className="w-full gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
              size="lg"
            >
              <Award className="h-5 w-5" />
              View Your Certificate
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleShare}
                className="flex-1 gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExploreCourses}
                className="flex-1 gap-2"
              >
                <BookOpen className="h-4 w-4" />
                More Courses
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseCompletionCelebration;
